import { uiText } from "@/lib/i18n/uiText";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { forwardRef } from "react";

interface LegalDisclaimerProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "compact" | "footer";
}

export const LegalDisclaimer = forwardRef<HTMLDivElement, LegalDisclaimerProps>(
  ({ variant = "default", className, ...props }, ref) => {
    if (variant === "compact") {
      return (
        <p ref={ref as React.Ref<HTMLParagraphElement>} className={cn("text-xs text-muted-foreground text-center", className)} {...props}>
           {uiText("Predictions are for entertainment only. High odds = high risk. Gamble responsibly.")} </p>
      );
    }

    if (variant === "footer") {
      return (
        <div ref={ref} className={cn("border-t border-border bg-card/50 py-4 px-4 sm:px-6", className)} {...props}>
          <div className="container mx-auto max-w-6xl space-y-3">
            <p className="text-xs sm:text-sm font-medium text-gradient-gold text-center">
               {uiText("Statlign — Sports Intelligence Platform · Beyond Prediction. Into Intelligence.")} </p>
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-medium">
                   {uiText("Predictions are for informational and entertainment purposes only.")} </p>
                <p>
                   {uiText("High odds (high odds) represent high risk. Past performance does not guarantee future results. Always gamble responsibly. If you have concerns about gambling, visit")}{" "}
                  <a 
                    href="https://www.begambleaware.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
                  >
                    BeGambleAware.org
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div 
        ref={ref}
        className={cn(
          "rounded-lg border border-warning/30 bg-warning/5 p-4",
          className
        )}
        {...props}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
               {uiText("Important Disclaimer")} </p>
            <p className="text-sm text-muted-foreground">
               {uiText("Predictions are for informational and entertainment purposes only. Higher odds represent higher risk. Past performance does not guarantee future results. Always gamble responsibly.")} </p>
            <p className="text-xs text-muted-foreground">
               {uiText("If you have concerns about gambling, visit")}{" "}
              <a 
                href="https://www.begambleaware.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
              >
                BeGambleAware.org
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }
);

LegalDisclaimer.displayName = "LegalDisclaimer";
